import type { Plan } from '@/types';
import type { SortDescriptor } from '@react-types/shared';

import React from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Button, Chip, Tooltip } from '@heroui/react';
import { RiAddLine, RiEditLine, RiDeleteBinLine } from '@remixicon/react';

import { TablePage, TableFilters, ColumnsToggleDropdown, FilterSelect } from '@/components/table';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { ConfirmDeleteModal } from '@/components/shared/ConfirmDeleteModal';
import { PlanFormModal } from '@/components/plans';
import { plansListParamsAtom, plansQueryAtom } from '@/store';
import { usePlansFilters } from '@/hooks/plans';
import { useVisibleColumns } from '@/hooks/table';
import { usePlanDelete, usePlanForm } from '@/hooks/plans';

export default function PlansPage() {
  const { data, isLoading, isError, error } = useAtomValue(plansQueryAtom);
  const [params, setParams] = useAtom(plansListParamsAtom);

  const {
    clearFilters,
    handlePageChange,
    hasAnyFilter,
    sortParam,
    setSortParam,
    searchFields,
    setSearchFields
  } = usePlansFilters();

  const [activeFilter, setActiveFilter] = React.useState<string>(
    searchFields.is_active === '' ? 'all' : searchFields.is_active
  );

  React.useEffect(() => {
    setActiveFilter(searchFields.is_active === '' ? 'all' : searchFields.is_active);
  }, [searchFields.is_active]);

  const handleClearFilters = React.useCallback(() => {
    setActiveFilter('all');
    clearFilters();
  }, [clearFilters]);

  const handleActiveFilterChange = React.useCallback(
    (value: string) => {
      setActiveFilter(value);
      setSearchFields(() => ({
        is_active: value === 'all' ? '' : value
      }));
    },
    [setSearchFields]
  );

  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor | undefined>(undefined);

  React.useEffect(() => {
    if (!sortParam) {
      setSortDescriptor(undefined);
      return;
    }
    const direction = sortParam.startsWith('-') ? 'descending' : 'ascending';
    const column = sortParam.replace(/^-/, '');
    setSortDescriptor({ column, direction });
  }, [sortParam]);

  const handleSortChange = React.useCallback(
    (descriptor: SortDescriptor) => {
      setSortDescriptor(descriptor);
      const column = descriptor.column?.toString();
      if (!column) {
        setSortParam(null);
        return;
      }
      const prefix = descriptor.direction === 'descending' ? '-' : '';
      setSortParam(`${prefix}${column}`);
    },
    [setSortParam]
  );

  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  const plans = React.useMemo<Plan[]>(() => data?.data ?? [], [data]);

  const {
    isOpen: isPlanFormOpen,
    onOpenChange: onPlanFormOpenChange,
    openCreate,
    openEdit,
    savePlan,
    isSubmitting,
    isEditMode,
    formData,
    setFormData
  } = usePlanForm();

  const {
    isOpen: isDeleteOpen,
    onOpenChange: onDeleteOpenChange,
    openDelete,
    confirmDelete,
    isDeleting,
    pendingPlan
  } = usePlanDelete();

  const columns = React.useMemo(
    () => [
      { name: 'Name', uid: 'name', sortable: true },
      { name: 'Slug', uid: 'slug' },
      { name: 'Price', uid: 'price' },
      { name: 'Currency', uid: 'currency' },
      { name: 'Monthly Event Limit', uid: 'credit_amount' },
      { name: 'Active', uid: 'is_active', align: 'center', headerAlign: 'center' },
      { name: 'Sort order', uid: 'sort_order', sortable: true },
      { name: 'Actions', uid: 'actions', align: 'end' }
    ],
    []
  );

  const renderCell = React.useCallback(
    (plan: Plan, columnKey: React.Key) => {
      switch (columnKey) {
        case 'name':
          return (
            <div className="flex flex-col">
              <span className="font-semibold">{plan.name}</span>
              <span className="text-default-400 text-xs">#{plan.id}</span>
            </div>
          );
        case 'slug':
          return <span className="text-default-600 text-sm">{plan.slug}</span>;
        case 'price':
          return <span className="text-sm">{Number(plan.price)}</span>;
        case 'currency':
          return <span className="text-sm">{plan.currency || '—'}</span>;
        case 'credit_amount':
          return (
            <span className="text-sm" title="Events per month">
              {plan.credit_amount.toLocaleString()}
            </span>
          );
        case 'is_active':
          return (
            <div className="flex justify-center">
              <Chip size="sm" variant="flat" color={plan.is_active ? 'success' : 'default'}>
                {plan.is_active ? 'Active' : 'Inactive'}
              </Chip>
            </div>
          );
        case 'sort_order':
          return <span className="text-sm">{plan.sort_order}</span>;
        case 'actions':
          return (
            <div className="flex items-center justify-end gap-2">
              <PermissionGuard permission="Plan.update">
                <Tooltip content="Edit plan">
                  <Button isIconOnly size="sm" variant="light" onPress={() => openEdit(plan)}>
                    <RiEditLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission="Plan.destroy">
                <Tooltip content="Delete plan">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => openDelete(plan)}
                  >
                    <RiDeleteBinLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </PermissionGuard>
            </div>
          );
        default:
          return null;
      }
    },
    [openDelete, openEdit]
  );

  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  return (
    <>
      <TablePage
        columns={columns}
        currentPage={data?.meta?.current_page}
        data={plans}
        emptyMessage="No plans found"
        error={error}
        errorMessage="Failed to load plans"
        title="Plans"
        titleActions={
          <PermissionGuard permission="Plan.store">
            <Button
              color="success"
              size="sm"
              variant="flat"
              startContent={<RiAddLine className="h-4 w-4" />}
              onPress={openCreate}
            >
              Add Plan
            </Button>
          </PermissionGuard>
        }
        filtersSection={
          <TableFilters
            hasActiveFilters={hasAnyFilter}
            isRefreshing={isLoading}
            onClearFilters={handleClearFilters}
            onRefresh={triggerRefresh}
          >
            <ColumnsToggleDropdown
              allColumns={columns.map((column) => ({ uid: column.uid, name: column.name }))}
              selected={visibleColumns}
              onChange={setVisibleColumns}
            />

            <FilterSelect
              label="Status"
              value={activeFilter}
              options={[
                { key: 'all', label: 'All' },
                { key: 'true', label: 'Active' },
                { key: 'false', label: 'Inactive' }
              ]}
              onChange={handleActiveFilterChange}
              className="w-40"
            />
          </TableFilters>
        }
        headerColumns={headerColumns}
        isError={isError}
        isLoading={isLoading}
        renderCell={renderCell}
        sortDescriptor={sortDescriptor}
        totalPages={data?.meta?.last_page}
        onPageChange={handlePageChange}
        onRefresh={triggerRefresh}
        onSortChange={handleSortChange}
      />

      <PlanFormModal
        isOpen={isPlanFormOpen}
        onOpenChange={onPlanFormOpenChange}
        formData={formData}
        setFormData={setFormData}
        isSubmitting={isSubmitting}
        onSubmit={savePlan}
        isEditMode={isEditMode}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onOpenChange={onDeleteOpenChange}
        title="Delete Plan"
        message={
          pendingPlan
            ? `Are you sure you want to delete the plan "${pendingPlan.name}"?`
            : 'Are you sure you want to delete this plan?'
        }
        confirmText="Delete Plan"
        isLoading={isDeleting}
        onConfirm={confirmDelete}
      />
    </>
  );
}
